import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from './usePermissions'

export interface ProductService {
  id: string
  organizationId: string
  name: string
  description?: string
  sku?: string
  hsnSacId: string | null
  category: 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE'
  defaultRate: number
  /** Minimum selling rate allowed after discount; 0/undefined = no minimum configured. */
  baseRate?: number
  currency: string
  unitId: string | null
  taxMasterId: string | null
  taxability: 'taxable' | 'exempt' | 'nil'
  isActive: boolean
  hsnSac?: { id: string; code: string; description: string }
  unit?: { id: string; name: string; shortCode: string; displayName: string }
  taxMaster?: { id: string; name: string; code?: string; treatment?: string; rate: number }
  createdAt: string
  updatedAt: string
}

export interface ProductServiceFilters {
  category?: string
  isActive?: boolean
  search?: string
}

export function useProductServices(filters?: ProductServiceFilters) {
  const { organization } = useAuth()
  const { can } = usePermissions()
  return useQuery({
    queryKey: ['productServices', organization?.id, filters],
    queryFn: async () => {
      const params = new URLSearchParams({ organizationId: organization?.id || '' })
      if (filters?.category) params.set('category', filters.category)
      if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
      if (filters?.search) params.set('search', filters.search)
      const res = await api.get<any>(`/master-data/products-services?${params}`)
      return (res?.data || []) as ProductService[]
    },
    enabled: !!organization?.id && can('QUOTATIONS', 'VIEW'),
    staleTime: 5 * 60 * 1000,
  })
}

export function useProductServiceMutations() {
  const { organization } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['productServices', organization?.id] })
    // Quotation/proforma forms read services through the consolidated master-data query.
    queryClient.invalidateQueries({ queryKey: ['documentMasterData'] })
  }

  const create = useMutation({
    mutationFn: (payload: Omit<ProductService, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'hsnSac' | 'unit' | 'taxMaster'>) =>
      api.post<any>('/master-data/products-services', { ...payload, organizationId: organization?.id }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ProductService> }) =>
      api.patch<any>(`/master-data/products-services/${id}`, payload, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setStatus = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<any>(`/master-data/products-services/${id}/status`, { isActive }, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  const setAllStatus = useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<{ message: string; modified: number }>(
        '/master-data/products-services/bulk-status',
        { isActive, organizationId: organization?.id },
        { skipErrorToast: true }
      ),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<any>(`/master-data/products-services/${id}`, { skipErrorToast: true }),
    onSuccess: invalidate,
  })

  return { create, update, setStatus, setAllStatus, remove }
}